import { cssRaw } from 'typestyle';

/**
 * Styling for the active calling screen -- the one a caller lives in all day.
 *
 * What this replaces:
 *  - outcome buttons coloured with magic 3-char hexes (#23c/#c32/#3c2/#ccc)
 *    and sized by percentage strings that changed with the button count
 *  - a retailer identity rendered as a generic two-column label/value grid,
 *    so the phone number -- the single thing the caller needs -- had the same
 *    weight as "Class"
 *  - a status panel of bare <h4>s
 *  - the queue rendered as full-width solid colour bars
 *
 * Scoped under .cs-call so none of it can reach other screens.
 */
cssRaw(`
  .cs-call {
    --ink: #0F2547; --muted: #52658A; --line: rgba(15,37,71,0.10);
    --blue: #1E6FD9;
    --ok: #16A34A; --warn: #D97706; --bad: #DC2626; --off: #64748B;
  }

  /* ---------- retailer identity ---------- */
  .cs-call__retailer { display: flex; flex-direction: column; gap: 14px; }
  .cs-call__firm {
    margin: 0; font-size: 22px; font-weight: 700; letter-spacing: -0.01em; color: var(--ink);
  }
  .cs-call__sub { margin: 3px 0 0; font-size: 13px; color: var(--muted); }
  .cs-call__meta { display: flex; flex-wrap: wrap; gap: 7px; }
  .cs-call__tag {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 3px 10px; border-radius: 999px;
    background: rgba(15,37,71,0.06); color: var(--muted);
    font-size: 12px; font-weight: 600;
  }

  /* ---------- phone tiles: the hero of this screen ---------- */
  .cs-call__phones { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  @media (max-width: 720px) { .cs-call__phones { grid-template-columns: 1fr; } }
  .cs-call__phone {
    display: flex; align-items: center; justify-content: space-between; gap: 10px;
    padding: 12px 14px;
    border: 1px solid var(--line); border-radius: 12px;
    background: #F7F9FD;
    transition: border-color .18s ease, background-color .18s ease, box-shadow .18s ease;
  }
  .cs-call__phone--active {
    border-color: rgba(30,111,217,0.55);
    background: rgba(30,111,217,0.07);
    box-shadow: 0 0 0 3px rgba(30,111,217,0.12);
  }
  /* Not yet in play -- dimmed, never hidden. The old code painted a black
     background under black text, which made the number invisible. */
  .cs-call__phone--idle { opacity: .62; }
  .cs-call__phone--dnd { border-color: rgba(220,38,38,0.45); background: rgba(220,38,38,0.05); }
  .cs-call__phone-label {
    display: block; font-size: 10.5px; font-weight: 700; letter-spacing: .07em;
    text-transform: uppercase; color: var(--muted); margin-bottom: 3px;
  }
  .cs-call__phone-number {
    display: block; font-size: 19px; font-weight: 700; letter-spacing: .01em;
    color: var(--ink); font-variant-numeric: tabular-nums;
  }
  .cs-call__phone--dnd .cs-call__phone-number { color: var(--bad); }
  .cs-call__badge {
    padding: 2px 8px; border-radius: 999px;
    font-size: 10px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase;
  }
  .cs-call__badge--dnd { background: rgba(220,38,38,0.13); color: var(--bad); }
  .cs-call__badge--live { background: rgba(30,111,217,0.14); color: #1653A8; }
  .cs-call__badge--none { background: rgba(15,37,71,0.07); color: var(--muted); }

  /* ---------- outcome buttons ---------- */
  .cs-call__outcomes {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 10px;
  }
  .cs-call__btn {
    position: relative;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 6px;
    min-height: 76px; padding: 12px 10px;
    border: 1px solid transparent; border-radius: 13px;
    font-size: 13.5px; font-weight: 700; letter-spacing: .01em;
    color: #fff; cursor: pointer;
    transition: transform .16s ease, box-shadow .16s ease, filter .16s ease;
  }
  .cs-call__btn svg { font-size: 19px; }
  .cs-call__btn:hover:not([disabled]) { transform: translateY(-2px); filter: brightness(1.06); }
  .cs-call__btn:active:not([disabled]) { transform: translateY(0); }
  .cs-call__btn:focus-visible { outline: 3px solid rgba(30,111,217,0.55); outline-offset: 2px; }
  .cs-call__btn[disabled] { opacity: .38; cursor: not-allowed; filter: grayscale(.35); }
  .cs-call__btn--answered { background: linear-gradient(135deg,#22C55E 0%,#16A34A 100%);
                            box-shadow: 0 5px 16px rgba(22,163,74,0.30); }
  .cs-call__btn--noreply  { background: linear-gradient(135deg,#F59E0B 0%,#D97706 100%);
                            box-shadow: 0 5px 16px rgba(217,119,6,0.28); }
  .cs-call__btn--wrong    { background: linear-gradient(135deg,#EF4444 0%,#DC2626 100%);
                            box-shadow: 0 5px 16px rgba(220,38,38,0.28); }
  .cs-call__btn--reject   { background: linear-gradient(135deg,#94A3B8 0%,#64748B 100%);
                            box-shadow: 0 5px 16px rgba(100,116,139,0.26); }
  /* Keyboard hint -- these four are pressed hundreds of times a day. */
  .cs-call__key {
    position: absolute; top: 7px; right: 9px;
    min-width: 17px; height: 17px; padding: 0 4px;
    display: inline-flex; align-items: center; justify-content: center;
    border-radius: 5px;
    background: rgba(255,255,255,0.26);
    font-size: 10px; font-weight: 800;
  }
  .cs-call__hint {
    margin: 10px 0 0; font-size: 11.5px; color: var(--muted); text-align: center;
  }
  .cs-call__hint kbd {
    display: inline-block; padding: 1px 5px; margin: 0 1px;
    border: 1px solid var(--line); border-bottom-width: 2px; border-radius: 4px;
    background: #fff; font-size: 10.5px; font-weight: 700; color: var(--ink);
  }

  /* ---------- status panel ---------- */
  .cs-call__row {
    display: flex; align-items: baseline; justify-content: space-between; gap: 12px;
    padding: 8px 0; border-bottom: 1px solid rgba(15,37,71,0.06);
  }
  .cs-call__row:last-child { border-bottom: none; }
  .cs-call__row-label { font-size: 12.5px; font-weight: 600; color: var(--muted); }
  .cs-call__row-value { font-size: 13.5px; font-weight: 600; color: var(--ink); text-align: right; }

  .cs-call__progress { margin: 4px 0 10px; }
  .cs-call__bar { height: 7px; border-radius: 99px; background: rgba(15,37,71,0.08); overflow: hidden; }
  .cs-call__bar span {
    display: block; height: 100%; border-radius: 99px;
    background: linear-gradient(90deg,#38BDF8 0%,#1E6FD9 100%);
    transition: width .45s cubic-bezier(.4,0,.2,1);
  }
  .cs-call__figures {
    display: flex; justify-content: space-between; gap: 8px;
    margin-top: 5px; font-size: 12px; color: var(--muted);
  }
  .cs-call__figures b { color: var(--ink); }

  /* ---------- queue ----------
     Was a list of full-width solid colour bars, which made a long queue a wall
     of colour and put white text on mid-tone fills. Now a dot carries status. */
  .cs-call__queue { max-height: 260px; overflow-y: auto; margin: 0; padding: 0; list-style: none; }
  .cs-call__queue::-webkit-scrollbar { width: 6px; }
  .cs-call__queue::-webkit-scrollbar-thumb { background: rgba(15,37,71,0.18); border-radius: 3px; }
  .cs-call__q {
    display: flex; align-items: center; gap: 9px;
    padding: 7px 8px; border-radius: 8px;
    font-size: 12.5px; color: var(--ink);
  }
  .cs-call__q + .cs-call__q { border-top: 1px solid rgba(15,37,71,0.05); }
  .cs-call__q-dot { flex: 0 0 8px; width: 8px; height: 8px; border-radius: 50%; background: rgba(15,37,71,0.18); }
  .cs-call__q-name { flex: 1 1 auto; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .cs-call__q-state { font-size: 10.5px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; color: var(--muted); }
  .cs-call__q--answered .cs-call__q-dot { background: var(--ok); }
  .cs-call__q--answered .cs-call__q-state { color: var(--ok); }
  .cs-call__q--noreply  .cs-call__q-dot { background: var(--warn); }
  .cs-call__q--noreply  .cs-call__q-state { color: var(--warn); }
  .cs-call__q--wrong    .cs-call__q-dot { background: var(--bad); }
  .cs-call__q--wrong    .cs-call__q-state { color: var(--bad); }
  .cs-call__q--rejected .cs-call__q-dot { background: var(--off); }
  .cs-call__q--rejected .cs-call__q-state { color: var(--off); }
  .cs-call__q--current { background: rgba(30,111,217,0.09); box-shadow: inset 2px 0 0 var(--blue); font-weight: 700; }
`);
